import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AssignmentTurnedIn,
  Autorenew,
  CardMembership,
  ChevronRight,
  Dashboard,
  HelpOutline,
  Logout,
  Menu,
  NotificationsNone,
  People,
  Search,
  MonitorHeart,
  Settings,
  SupportAgent,
} from '@mui/icons-material';
import { tokens } from '../../theme';
import { adminTokens } from './adminTokens';
import cardMountains from '../../assets/card-mountains.png';

const { border: BORDER, borderStrong: BORDER_STRONG, drawerWidth: DRAWER_WIDTH, editorialSerif } = adminTokens;

const adminNav = [
  { id: 'overview', label: 'Platform Overview', icon: <Dashboard fontSize="small" /> },
  { id: 'customers', label: 'Customers', icon: <People fontSize="small" /> },
  { id: 'subscriptions', label: 'Subscriptions', icon: <CardMembership fontSize="small" /> },
  { id: 'operations', label: 'Support Queue', icon: <SupportAgent fontSize="small" />, badgeKey: 'tickets' },
  { id: 'review', label: 'Content Review', icon: <AssignmentTurnedIn fontSize="small" />, badgeKey: 'submissions' },
  { id: 'waitlist', label: 'Waitlist', icon: <People fontSize="small" /> },
  { id: 'system', label: 'System Health', icon: <MonitorHeart fontSize="small" /> },
  { id: 'settings', label: 'Settings', icon: <Settings fontSize="small" /> },
];

export default function AdminShell({
  activeView,
  currentUser,
  search,
  onSearchChange,
  onNavigate,
  onRefresh,
  onOpenOperations,
  onLogout,
  badges = {},
  children,
  footer,
  contentFill = false,
}) {
  const isDesktop = useMediaQuery('(min-width:1180px)');
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const shellHeight = isDesktop ? `calc(100vh / ${adminTokens.desktopZoom})` : '100vh';

  const sidebar = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundImage: `linear-gradient(180deg, rgba(7,15,12,0.82), rgba(7,15,12,0.97)), url(${cardMountains})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
      }}
    >
      <Box sx={{ p: 2.25, borderBottom: `1px solid ${BORDER}` }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
          <Box sx={{ width: 28, height: 28, border: `1px solid ${BORDER_STRONG}`, display: 'grid', placeItems: 'center', color: tokens.accentStrong, fontSize: '0.85rem' }}>🌲</Box>
          <Typography variant="h6" sx={{ color: tokens.cream, fontFamily: editorialSerif, fontWeight: 500 }}>
            FastTime Admin
          </Typography>
        </Stack>
        <Typography variant="caption" sx={{ color: tokens.textMuted, pl: 0.5 }}>
          Admin Workspace
        </Typography>
      </Box>

      <List sx={{ px: 1.25, py: 1.25, flex: 1, overflow: 'auto' }}>
        {adminNav.map((item) => {
          const selected = item.id === activeView;
          const badgeCount = item.badgeKey ? badges[item.badgeKey] : 0;
          return (
            <ListItemButton
              key={item.id}
              selected={selected}
              onClick={() => {
                onNavigate(item.id);
                setMobileOpen(false);
              }}
              sx={{
                mb: 0.35,
                borderRadius: 0,
                border: `1px solid ${selected ? BORDER_STRONG : 'transparent'}`,
                '&.Mui-selected': { backgroundColor: 'rgba(169,191,122,0.12)' },
                '&:hover': { backgroundColor: 'rgba(255,255,255,0.04)' },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32, color: selected ? tokens.accentStrong : tokens.textMuted }}>
                {item.icon}
              </ListItemIcon>
              <ListItemText
                primary={item.label}
                primaryTypographyProps={{ color: selected ? tokens.cream : tokens.textSoft, fontSize: '0.88rem' }}
              />
              {badgeCount > 0 && (
                <Chip
                  label={badgeCount}
                  size="small"
                  sx={{
                    height: 20,
                    minWidth: 20,
                    borderRadius: 0,
                    fontSize: '0.68rem',
                    bgcolor: item.badgeKey === 'tickets' ? 'rgba(230,124,115,0.18)' : 'rgba(212,144,99,0.18)',
                    color: item.badgeKey === 'tickets' ? tokens.danger : tokens.clay,
                    border: `1px solid ${BORDER}`,
                  }}
                />
              )}
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ flexShrink: 0, borderTop: `1px solid ${BORDER}` }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <Box sx={{ p: 1.5, border: `1px solid ${BORDER}`, backgroundColor: 'rgba(9,14,12,0.62)' }}>
            <Typography variant="caption" sx={{ color: tokens.textMuted, display: 'block' }}>Premium plan</Typography>
            <Typography variant="body2" sx={{ color: tokens.cream, mt: 0.3 }}>Store-managed billing</Typography>
            <Typography variant="caption" sx={{ color: tokens.accentStrong }}>Revenue share 70%</Typography>
          </Box>
        </Box>

        <Box sx={{ px: 2, pb: 2, borderTop: `1px solid ${BORDER}` }}>
          <Stack direction="row" spacing={1.25} alignItems="center" sx={{ pt: 2, mb: 1.25 }}>
            <Avatar sx={{ width: 34, height: 34, bgcolor: 'rgba(169,191,122,0.18)', color: tokens.accentStrong, fontSize: '0.8rem' }}>
              {(currentUser?.email || 'A').slice(0, 2).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" sx={{ color: tokens.cream }} noWrap>Admin</Typography>
              <Typography variant="caption" sx={{ color: tokens.textMuted }} noWrap>{currentUser?.email}</Typography>
            </Box>
            <ChevronRight sx={{ color: tokens.textMuted, fontSize: 16 }} />
          </Stack>
          <Button fullWidth variant="outlined" size="small" startIcon={<Logout />} onClick={onLogout}>
            Sign out
          </Button>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: shellHeight,
        ...(isDesktop ? { height: shellHeight } : {}),
        display: 'flex',
        bgcolor: adminTokens.bg,
        ...(isDesktop ? { zoom: adminTokens.desktopZoom } : {}),
      }}
    >
      {isDesktop ? (
        <Box sx={{ width: DRAWER_WIDTH, flexShrink: 0, borderRight: `1px solid ${BORDER}` }}>
          <Box sx={{ position: 'sticky', top: 0, height: shellHeight }}>{sidebar}</Box>
        </Box>
      ) : (
        <Drawer open={mobileOpen} onClose={() => setMobileOpen(false)} PaperProps={{ sx: { width: DRAWER_WIDTH, bgcolor: adminTokens.bg } }}>
          {sidebar}
        </Drawer>
      )}

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            flexShrink: 0,
            px: { xs: 2, md: 2.5 },
            py: 1,
            borderBottom: `1px solid ${BORDER}`,
            backdropFilter: 'blur(22px)',
            backgroundColor: 'rgba(8,17,13,0.88)',
          }}
        >
          <Stack direction="row" spacing={1.25} alignItems="center">
            {!isDesktop && (
              <IconButton onClick={() => setMobileOpen(true)} sx={{ color: tokens.textSoft }}>
                <Menu />
              </IconButton>
            )}
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1, minWidth: 0, maxWidth: 520, border: `1px solid ${BORDER}`, px: 1.25, py: 0.35 }}>
              <Search sx={{ color: tokens.textMuted, fontSize: 18 }} />
              <TextField
                variant="standard"
                value={search}
                onChange={(e) => onSearchChange(e.target.value)}
                placeholder="Search cases, users, emails..."
                InputProps={{ disableUnderline: true, sx: { fontSize: '0.88rem' } }}
                sx={{ minWidth: 0, flex: 1 }}
              />
              <Typography variant="caption" sx={{ color: tokens.textMuted, border: `1px solid ${BORDER}`, px: 0.6, py: 0.1 }}>⌘K</Typography>
            </Stack>
            <Badge badgeContent={badges.tickets || 0} color="error" sx={{ '& .MuiBadge-badge': { fontSize: '0.65rem' } }}>
              <IconButton sx={{ color: tokens.textSoft }} onClick={onOpenOperations} aria-label="Open support queue">
                <NotificationsNone fontSize="small" />
              </IconButton>
            </Badge>
            <IconButton sx={{ color: tokens.textSoft }} onClick={onOpenOperations} aria-label="Open support workspace">
              <HelpOutline fontSize="small" />
            </IconButton>
            <Button variant="outlined" size="small" onClick={onRefresh} startIcon={<Autorenew sx={{ fontSize: 16 }} />}>
              Sync
            </Button>
          </Stack>
        </Box>

        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: contentFill ? 'hidden' : 'auto',
            px: { xs: 2, md: 2.5 },
            py: { xs: 2, md: 2.2 },
          }}
        >
          {children}
        </Box>

        {footer && (
          <Box
            sx={{
              flexShrink: 0,
              px: 2.5,
              py: 1,
              borderTop: `1px solid ${BORDER}`,
              backgroundColor: 'rgba(8,17,13,0.9)',
            }}
          >
            {footer}
          </Box>
        )}
      </Box>
    </Box>
  );
}
